/**
 * The body of one message, sanitised and with its quoted chain folded away.
 *
 * Shared by the two places a message is read: a stored thread (`MessageBubble`)
 * and a message read straight off the IMAP server (`imap/ImapMessagePane`).
 * They arrive by different routes and neither has been through the other's
 * code, so the sanitising has to live in one place - a second copy is a second
 * chance to get an allowlist subtly wrong.
 *
 * Sanitising is NOT the same for the two, because they are not equally trusted
 * and stripping them alike was already losing information. That split, and the
 * allowlists it needs, live in `mailHtml.ts`; this file is about what the
 * cleaned message then looks like on the page.
 *
 * And looking right takes more than a coat of paint. A mail is laid out for a
 * 600px column in somebody else's client: its tables carry hard widths, its
 * images carry their own, and with the inline styles gone there is nothing left
 * to hold any of it inside our pane. `.crm-mail-html` constrains what it can and
 * gives what it cannot its own horizontal scroll, so a wide letter scrolls
 * inside its own bubble instead of dragging the whole reading pane sideways and
 * pushing the text off the screen. Before that class existed this div asked for
 * `prose prose-sm dark:prose-invert`, and none of those three class names has
 * ever been defined in this repo - the typography plugin is not installed - so
 * every HTML message was rendered with no rules at all.
 *
 * The quoted reply chain is folded away (see quotedText.ts). The split runs on
 * the sanitiser's output, never on the raw body - that order is what keeps the
 * fold from being a hole in the sanitising.
 */
import { useState, type ReactNode } from "react";
import { MoreHorizontal } from "lucide-react";
import { sanitizeMailHtml } from "./mailHtml";
import type { T } from "./shared";
import { splitQuotedHtml, splitQuotedText } from "./quotedText";

/**
 * The chain, one click away.
 *
 * Collapsed by default and drawn as the same "..." every mail client uses: the
 * shape is already known, and a sentence here would compete for attention with
 * the message it sits under. The label lives on the button's title and its
 * accessible name, where it costs nothing visually.
 */
function QuoteFold({ t, children }: { t: T; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const label = t(open ? "email.quotedHide" : "email.quotedShow");
  return (
    <div className="mt-2">
      <button
        type="button"
        className="crm-quote-toggle"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => setOpen((v) => !v)}
      >
        <MoreHorizontal className="size-3.5" />
      </button>
      {open && children}
    </div>
  );
}

export function MailBody({
  t,
  html,
  text,
  trusted = false,
}: {
  t: T;
  html: string | null | undefined;
  text: string | null | undefined;
  /** True only for HTML this CRM generated itself. */
  trusted?: boolean;
}) {
  const clean = html ? sanitizeMailHtml(html, trusted) : null;
  const body = clean !== null ? splitQuotedHtml(clean) : splitQuotedText(text ?? "");

  if (clean !== null) {
    // Containment for both; our typography only for the message that lost its
    // own. A campaign letter styles itself inline on purpose and repainting it
    // in the CRM's colours puts near-white text on its own white card.
    const cls = trusted ? "crm-mail-html" : "crm-mail-html is-received";
    return (
      <>
        <div className={cls} dangerouslySetInnerHTML={{ __html: body.main }} />
        {body.quoted && (
          <QuoteFold t={t}>
            <div className={`${cls} crm-quote mt-2`} dangerouslySetInnerHTML={{ __html: body.quoted }} />
          </QuoteFold>
        )}
      </>
    );
  }

  return (
    <>
      {/* No `text-sm`: it used to pin the body at 14px, one step above the
          13.75px the reading pane sets and out of reach of it. Both panes that
          render this are a `.crm-read-body`, so inheriting is both correct and
          the only way the compact density can reach the text at all. */}
      <p className="whitespace-pre-wrap">{body.main}</p>
      {body.quoted && (
        <QuoteFold t={t}>
          <p className="crm-quote mt-2 whitespace-pre-wrap">{body.quoted}</p>
        </QuoteFold>
      )}
    </>
  );
}
